import { z } from 'zod';
import { messageKey } from './messages.js';
/**
 * Сетки видеостены (§7.7, задача 9).
 *
 * `custom` — произвольная раскладка: ячейка задаётся прямоугольником
 * на сетке `CUSTOM_GRID_SIZE`, а не номером позиции.
 */
export const LAYOUT_GRIDS = ['1x1', '2x2', '3x3', '4x4', 'custom'];
export const layoutGridSchema = z.enum(LAYOUT_GRIDS);
/** Сторона поля произвольной раскладки в ячейках. */
export const CUSTOM_GRID_SIZE = 12;
/** Сколько ячеек в сетке. У `custom` предела нет — считается по списку. */
export function gridCellCount(grid) {
    switch (grid) {
        case '1x1':
            return 1;
        case '2x2':
            return 4;
        case '3x3':
            return 9;
        case '4x4':
            return 16;
        case 'custom':
            return null;
    }
}
/**
 * Какое качество берёт раскладка (задача 7).
 *
 * Правило простое и общее для всех мест, где показывается сетка: 2×2
 * и крупнее — substream, одиночная ячейка и полный экран — основной поток.
 * Оно живёт в контрактах, а не в компоненте, потому что по нему же считается
 * предупреждение об отсутствии substream на стороне api.
 */
export function qualityForGrid(grid, cellCount) {
    if (grid === '1x1') {
        return 'main';
    }
    if (grid === 'custom') {
        // У произвольной раскладки размер ячейки задаёт человек, и «крупнее 2×2»
        // определяется числом камер на экране, а не именем сетки.
        return cellCount > 1 ? 'sub' : 'main';
    }
    return 'sub';
}
/**
 * Ячейка раскладки. `position` — порядковый номер в сетке, слева направо
 * и сверху вниз; `x`/`y`/`w`/`h` заполняются только у `custom`.
 */
export const layoutCellSchema = z.object({
    position: z.number().int().min(0).max(255),
    cameraId: z.uuid(),
    x: z
        .number()
        .int()
        .min(0)
        .max(CUSTOM_GRID_SIZE - 1)
        .optional(),
    y: z
        .number()
        .int()
        .min(0)
        .max(CUSTOM_GRID_SIZE - 1)
        .optional(),
    w: z.number().int().min(1).max(CUSTOM_GRID_SIZE).optional(),
    h: z.number().int().min(1).max(CUSTOM_GRID_SIZE).optional(),
});
export const cameraLayoutSchema = z.object({
    id: z.uuid(),
    name: z.string(),
    grid: layoutGridSchema,
    cells: z.array(layoutCellSchema),
    /** `null` — общая раскладка организации, видна всем (§7.7). */
    userId: z.uuid().nullable(),
});
/**
 * Ячейки без дублей: одна позиция — одна камера.
 *
 * Проверяется схемой, а не интерфейсом: раскладку можно сохранить и запросом,
 * а две камеры в одной ячейке — это не «странно выглядит», это неразрешимо
 * при загрузке.
 */
const uniquePositions = (cells) => new Set(cells.map((cell) => cell.position)).size === cells.length;
export const saveLayoutSchema = z
    .object({
    name: z.string().trim().min(1, messageKey('validation.layout.nameRequired')).max(100),
    grid: layoutGridSchema,
    cells: z.array(layoutCellSchema).max(64).default([]),
    /** Сохранить общей для организации. Требует права `org:admin`. */
    shared: z.boolean().default(false),
})
    .refine((value) => uniquePositions(value.cells), {
    message: messageKey('validation.layout.duplicateCell'),
    path: ['cells'],
})
    .refine((value) => {
    const limit = gridCellCount(value.grid);
    return limit === null || value.cells.every((cell) => cell.position < limit);
}, { message: messageKey('validation.layout.cellOutOfGrid'), path: ['cells'] });
//# sourceMappingURL=layouts.js.map